import { API_BASE_URL, STORAGE_KEYS } from '../constants';

export type DocumentFilenameSource = {
  id?: number;
  content?: { originalName?: unknown } | null;
  filePath?: string | null;
};

export type DocumentFetchEndpoint = 'download' | 'view';

export function getDocumentAuthToken(): string {
  return (
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('ACCESS_TOKEN') ||
    ''
  );
}

/** Parse RFC 5987 / legacy Content-Disposition filename. */
export function parseContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      // fall through
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const unquotedMatch = header.match(/filename=([^;\n]+)/i);
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1].trim().replace(/^["']|["']$/g, '');
  }

  return null;
}

export function basenameFromFilePath(filePath?: string | null): string {
  if (!filePath?.trim()) return '';
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}

/** Display / download name: Content-Disposition → originalName → basename(filePath). */
export function resolveDocumentFilename(
  doc?: DocumentFilenameSource | null,
  contentDisposition?: string | null
): string {
  const fromHeader = parseContentDisposition(contentDisposition);
  if (fromHeader?.trim()) return fromHeader.trim();

  const originalName =
    doc?.content &&
    typeof doc.content === 'object' &&
    typeof (doc.content as { originalName?: unknown }).originalName === 'string'
      ? String((doc.content as { originalName: string }).originalName).trim()
      : '';
  if (originalName) return originalName;

  const base = basenameFromFilePath(doc?.filePath);
  if (base) return base;

  return doc?.id != null ? `document-${doc.id}` : 'document';
}

export async function fetchDocumentResponse(
  documentId: number,
  endpoint: DocumentFetchEndpoint = 'download'
): Promise<Response> {
  const token = getDocumentAuthToken();
  const response = await fetch(`${API_BASE_URL}/documents/${documentId}/${endpoint}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response;
}

export async function fetchDocumentBlob(
  documentId: number,
  endpoint: DocumentFetchEndpoint = 'download'
): Promise<{ blob: Blob; filename: string; contentDisposition: string | null }> {
  const response = await fetchDocumentResponse(documentId, endpoint);
  const contentDisposition = response.headers.get('Content-Disposition');
  const blob = await response.blob();
  const filename =
    parseContentDisposition(contentDisposition) || `document-${documentId}`;
  return { blob, filename, contentDisposition };
}

export async function fetchDocumentObjectUrl(
  documentId: number,
  endpoint: DocumentFetchEndpoint = 'download'
): Promise<{ url: string; revoke: () => void; filename: string; contentDisposition: string | null }> {
  const { blob, filename, contentDisposition } = await fetchDocumentBlob(documentId, endpoint);
  const url = window.URL.createObjectURL(blob);
  return {
    url,
    revoke: () => window.URL.revokeObjectURL(url),
    filename,
    contentDisposition,
  };
}

export async function downloadDocument(
  documentId: number,
  doc?: DocumentFilenameSource | null
): Promise<string> {
  const response = await fetchDocumentResponse(documentId, 'download');
  const blob = await response.blob();
  const filename = resolveDocumentFilename(doc, response.headers.get('Content-Disposition'));
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return filename;
}

export async function viewDocument(documentId: number): Promise<void> {
  const { url, revoke } = await fetchDocumentObjectUrl(documentId, 'download');
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    revoke();
    throw new Error('POPUP_BLOCKED');
  }
  window.setTimeout(revoke, 60000);
}

export function documentPreviewEndpoint(fileType?: string | null): DocumentFetchEndpoint {
  return fileType === 'photo' ? 'view' : 'download';
}
